import { Component, Input, OnInit } from '@angular/core';
import { SecurityExportService, SecurityExport, SecurityExportRequest } from '../../../services/security-exports-service';
import { ClientDataSourceService, DataSourceModel } from '../../../services/client-data-source-service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogService } from '../../utility/dialog/dialog-service';
import { ModalType } from '../../utility/dialog-template/dialog-template.component';

@Component({
  selector: 'client-security-exports',
  templateUrl: './client-security-exports.component.html',
  styleUrls: ['./client-security-exports.component.css'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class ClientSecurityExportsComponent implements OnInit {
  @Input() clientId!: string;
  exports: SecurityExport[] = [];
  dataSources: DataSourceModel[] = [];

  loading = false;
  error: string | null = null;
  dateFormats: string[] = [
    'yyyy/MM/dd',
    'yyyy-MM-dd',
    'MM/dd/yyyy',
    'dd/MM/yyyy',
    'yyyyMMdd',
    'MMddyy',
    'M/d/yyyy',
    'd/M/yyyy'
  ];

  constructor(
    private service: SecurityExportService,
    private dataSourceService: ClientDataSourceService,
    private dialog: DialogService
  ) { }

  ngOnInit(): void {
    if (!this.clientId) return;

    this.loadExports();
    this.loadDataSources();
  }


  loadExports() {
    if (!this.clientId) return;

    this.loading = true;
    this.service.getSecurityExports(this.clientId, exports => {
      this.exports = exports.map(x => ({
        ...x,
        autoRun: x.autoRun ?? true,
        recipients: x.recipients?.length ? [...x.recipients] : [''],
        clientId: this.clientId!,
        editing: false
      }));
      this.loading = false;
    });
  }

  loadDataSources(): void {
    if (!this.clientId) return;

    this.dataSourceService.getDataSources(
      this.clientId,
      (all: any[]) => {
        this.dataSources = (all ?? [])
          .map((item: any) => ({
            DataSourceId: item.DataSourceId,
            name: item.Name ?? '',
            legacyEncryption: item.LegacyEncryption === true || item.LegacyEncryption === 'True',
            connectionType: (item.ConnectionType ?? 'File') as 'File' | 'Data',
            data: {}
          }))
          .filter(ds => ds.connectionType?.toLowerCase() === 'file');
      },
      (err: any) => {
        console.error('Failed to load data sources', err);
        this.error = 'Failed to load data sources.';
      }
    );
  }

  addExport() {
    if (!this.clientId) return;

    this.exports.unshift({
      clientId: this.clientId,
      enabled: true,
      autoRun: true,
      name: '',
      view: '',
      exportFileName: '',
      dataSourceId: '',
      dateFormat: '',
      recipients: [''],
      //schedule: '0 0 * * *', 

      isNew: true,
      editing: true
    });
  }


  addRecipient(se: SecurityExport) {
    (se.recipients ??= []).push('');
  }

  removeRecipient(se: SecurityExport, index: number) {
    se.recipients?.splice(index, 1);
    if (!se.recipients?.length) se.recipients = [''];
  }

  trackByIndex(index: number): number {
    return index;
  }

  editExport(se: SecurityExport) {
    se.editing = true;
  }

  cancelEdit(se: SecurityExport) {
    if (se.isNew) {
      this.exports = this.exports.filter(x => x !== se);
    } else {
      se.editing = false;
    }
  }
  deleteExport(se: SecurityExport) {
    if (!this.clientId) return;

    const clientId = this.clientId; 

    this.dialog.openModal(
      "Delete Security Export",
      `Are you sure you want to delete "${se.name}"?`,
      ModalType.QUESTION,
      () => {
        const req: SecurityExportRequest = {
          clientId: clientId, 
          exportId: se.recordId!
        };

        this.service.deleteSecurityExport(req, () => {
          this.exports = this.exports.filter(x => x !== se);
        });
      },
      () => { }
    );
  }


  runNow(se: SecurityExport) {
    if (!this.clientId) return; 

    const req: SecurityExportRequest = {
      clientId: this.clientId,
      exportId: se.recordId!
    };

    this.service.runSecurityExportNow(req, () => {
      this.dialog.openModal(
        'Security Export',
        'Security export started.',
        ModalType.INFO
      );
    });
  }

  saveExport(se: SecurityExport) {
    if (!this.validate(se) || !this.clientId) return;

    if (se.isNew) {
      this.service.addSecurityExport(this.clientId, se, (savedExport) => {
        Object.assign(se, savedExport);
        se.editing = false;
        se.isNew = false; 
      });
    } else {
      if (!se.recordId) {
        console.error("Cannot update export without recordId");
        return;
      }
      this.service.updateSecurityExport(this.clientId, se, () => {
        se.editing = false;
      });
    }
  }

  validate(se: SecurityExport): boolean {
    if (!se.name?.trim()) {
      this.error = 'Name is required.';
      return false;
    }
    if (!se.view?.match(/^[A-Za-z0-9_.]+$/)) {
      this.error = 'View name must contain only letters, numbers, underscores, or dots.';
      return false;
    }
    if (!se.exportFileName?.match(/^[A-Za-z0-9 _.-]+$/)) {
      this.error = 'Export file name contains invalid characters.';
      return false;
    }
    se.recipients = (se.recipients ?? []).map(r => r.trim());
    const badEmail = se.recipients.find(r => r && !this.isValidEmail(r));
    if (badEmail) {
      this.error = `Invalid recipient email: ${badEmail}`;
      return false;
    }
    if (se.dateFormat && !this.isValidDateFormat(se.dateFormat)) {
      this.error = 'Invalid date format.';
      return false;
    }

    // Needs somewhere to deliver: a file connection, email recipients, or both
    if (!se.dataSourceId && !se.recipients.some(r => r)) {
      this.error = 'Choose a file connection and/or add at least one recipient.';
      return false;
    }

    //// Cron checks
    //if (!se.schedule?.trim()) {
    //  this.error = 'Schedule is required.';
    //  return false;
    //}
    //const segments = se.schedule.trim().split(/\s+/);
    //if (segments.length !== 5) {
    //  this.error = 'Invalid schedule. Cron expression must have 5 segments.';
    //  return false;
    //}

    this.error = null;
    return true;
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  isValidDateFormat(fmt: string): boolean {
    return /^[yMd\/.\- ]+$/.test(fmt);
  }
}
